import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, ArrowRight, ChevronRight } from 'lucide-react';
import { trackBlogInteraction, trackButtonClick } from '../lib/analytics';
import { blogPosts, BlogPost } from '../data/blogPosts';

const BlogCard = ({ post }: { post: BlogPost }) => (
  <Link
    to={`/blog/${post.slug}`}
    onClick={() => trackBlogInteraction('read_more_click', post.title, post.id)}
    className="card-premium p-6 text-left group w-full block"
  >
    <div className="flex items-center justify-between mb-4">
      <span className="tag-pill">
        {post.category}
      </span>
      <span className="text-xs text-gray-500">{post.readingTime}</span>
    </div>

    <h3 className="text-[15px] font-medium text-white mb-3 group-hover:text-primary/90 transition-colors line-clamp-2">
      {post.title}
    </h3>

    <p className="text-gray-500 text-sm mb-4 line-clamp-3 leading-relaxed">
      {post.excerpt}
    </p>

    <div className="flex items-center justify-between text-sm text-gray-500">
      <div className="flex items-center">
        <Calendar className="h-3.5 w-3.5 mr-1.5 opacity-70" />
        <span>{post.publishedDate}</span>
      </div>
      <span className="text-primary/80 flex items-center group-hover:translate-x-1 transition-transform text-xs font-medium">
        Read more <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
      </span>
    </div>
  </Link>
);

const BlogPage = () => {
  const [filter, setFilter] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Blog | AI & Web Solutions IOM';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Practical website, local SEO and AI automation advice for Isle of Man and Channel Islands businesses.');
    return () => {
      document.title = 'Web Solutions | aiwebsolutions.im';
    };
  }, []);

  const categories = ['all', ...Array.from(new Set(blogPosts.map(p => p.category)))];

  const filteredPosts = filter === 'all'
    ? blogPosts
    : blogPosts.filter(p => p.category === filter);

  return (
    <div className="min-h-screen pt-24 lg:pt-28">
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="page-header animate-fade-in">
          <h1>Insights & Articles</h1>
          <p>
            Practical website, local SEO and AI automation advice for Isle of Man and Channel Islands businesses.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-12 lg:mb-16 animate-fade-in-delay-1" style={{ opacity: 0 }}>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`filter-btn ${
                filter === category
                  ? 'filter-btn-active'
                  : 'filter-btn-inactive'
              }`}
            >
              {category === 'all' ? 'All Articles' : category}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16 animate-fade-in-delay-2" style={{ opacity: 0 }}>
          {filteredPosts.map((post) => (
            <BlogCard key={post.id} post={post} />
          ))}
        </div>

        <div className="card-premium p-8 lg:p-10 text-center animate-fade-in-delay-3" style={{ opacity: 0 }}>
          <h2 className="text-xl font-semibold text-white mb-4">
            Want to discuss any of these topics?
          </h2>
          <p className="text-gray-500 mb-8 max-w-lg mx-auto leading-relaxed">
            We are always happy to chat about how these ideas might apply to your specific situation.
          </p>
          <button
            onClick={() => {
              trackButtonClick('Contact from Blog', 'Blog');
              navigate('/contact');
            }}
            className="btn-primary"
          >
            Get in Touch
            <ArrowRight className="ml-2 h-4 w-4 inline" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
